import {
  PrimaryButton,
  ConfirmationModal,
  useModalControl,
  Heading,
  SecondaryButton,
} from '@weave/design-system';
import {
  useChangeTagPercentageMutation,
  useCreateTagMutation,
  useDeleteTagMutation,
  useWipeTagMutation,
} from 'apis/wetags/mutations';
import {
  getTagQuery,
  getTagStatsQuery,
  listEntityTagsQuery,
  listTagsQuery,
} from 'apis/wetags/queriesV2';
import React, { useCallback, useState, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { Page } from 'components/shared';
import { getBreadcrumbs } from '../wetagsBreadcrumbs';
import { WetagsHeader } from '../components/WetagsHeader.component';
import { CreateEntityTagModal } from './CreateEntityTagModal.component';
import { ChangeTagPercentageModal } from './ChangeTagPercentageModal.component';
import { FormattedDate } from '../components/FormattedDate.component';
import { theme } from '@weave/theme';
import { useAlert } from '@weave/alert-system';
import {
  ReportTagStatsResponse,
  Tag,
} from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';
import { AllowDenyTagManager } from './AllowDenyTagManager.component';
import { DetailRow } from './DetailRow';

export function WetagsTagRoot() {
  const alerts = useAlert();
  const { tagId, namespaceId } = useParams<{ tagId: string; namespaceId: string }>();
  const isDisableTag = tagId.startsWith('disable-');

  const history = useHistory();
  const location = useLocation();
  const [createModalTagId, setCreateModalTagId] = useState<string | null>(null);
  const [percentageModalTarget, setPercentageModalTarget] = useState<{
    tagId: string;
    currentPercentage?: number;
  } | null>(null);
  const { modalProps, triggerProps } = useModalControl();
  const scrollPositionRef = useRef(0);

  const { data: tagData, isLoading: isTagLoading } = useQuery(
    getTagQuery.getUseQueryOptions({ namespaceId, tagId })
  );
  const { data: disableTagData } = useQuery(
    getTagQuery.getUseQueryOptions(
      { namespaceId, tagId: `disable-${tagId}` },
      {
        enabled: !isDisableTag,
        retry: false,
      }
    )
  );
  const { data: disableStatsData } = useQuery(
    getTagStatsQuery.getUseQueryOptions({ namespaceId, tagId: `disable-${tagId}` })
  );
  const { data: allowStatsData } = useQuery(
    getTagStatsQuery.getUseQueryOptions({ namespaceId, tagId })
  );

  const { mutateAsync: createTagAsync } = useCreateTagMutation();

  const { mutateAsync: deleteTagAsync, isLoading: isDeleting } = useDeleteTagMutation();
  const { mutateAsync: changePercentageAsync } = useChangeTagPercentageMutation();
  const { mutateAsync: wipeTagAsync } = useWipeTagMutation();

  const queryClient = useQueryClient();
  const onDelete = useCallback(async () => {
    try {
      await deleteTagAsync({ namespaceId, tagId });
      queryClient.removeQueries(
        getTagQuery.getUseQueryOptions({ namespaceId, tagId }).queryKey,
        { exact: true }
      );
      listTagsQuery.invalidate({ namespaceId });
      getTagQuery.invalidate({ namespaceId, tagId }).catch(() => {});
      getTagStatsQuery.invalidate({ namespaceId, tagId }).catch(() => {});
      history.replace(`/wetags/namespace/${namespaceId}`);
    } catch (error) {
      alerts.error('Failed to delete tag. Please try again.');
    }
  }, [tagId, namespaceId, history]);

  const onClickDisableTag = useCallback(async () => {
    if (!disableTagData) {
      /**
       * Disable tag does not exist, create it with 100% rollout.
       */
      await createDisableTag(100);
    } else {
      /**
       * Disable tag exists but percentage is 0, update percentage to 100% to disable the tag.
       */
      await disableTag();
    }
  }, [disableTagData]);

  const createDisableTag = useCallback(
    async (percentage?: number) => {
      try {
        await createTagAsync({
          tag: {
            namespaceId,
            id: `disable-${tagId}`,
            description: `This is a system-generated tag used to disable the tag "${tagId}". It is automatically created when disabling the original tag for the first time.`,
            percentage,
          },
        });
        getTagQuery.invalidate({ namespaceId, tagId: `disable-${tagId}` });
      } catch (error) {
        alerts.error('Failed to disable tag. Please try again.');
      }
    },
    [createTagAsync, namespaceId, tagId]
  );

  const disableTag = useCallback(async () => {
    try {
      await changePercentageAsync({
        namespaceId,
        tagId: `disable-${tagId}`,
        percentage: 100,
      });
      getTagQuery.invalidate({ namespaceId, tagId: `disable-${tagId}` });
      getTagStatsQuery.invalidate({ namespaceId, tagId: `disable-${tagId}` });
    } catch (error) {
      alerts.error('Failed to disable tag percentage. Please try again.');
    }
  }, [changePercentageAsync, namespaceId, tagId]);

  const enableTag = useCallback(async () => {
    try {
      await changePercentageAsync({
        namespaceId,
        tagId: `disable-${tagId}`,
        percentage: 0,
      });
      getTagQuery.invalidate({ namespaceId, tagId: `disable-${tagId}` });
      getTagStatsQuery.invalidate({ namespaceId, tagId: `disable-${tagId}` });
    } catch (error) {
      alerts.error('Failed to enable tag percentage. Please try again.');
    }
  }, [changePercentageAsync, namespaceId, tagId]);

  const onAllowAll = useCallback(async () => {
    try {
      const requests = [
        changePercentageAsync({
          namespaceId,
          tagId,
          percentage: 100,
        }),
      ];

      if (disableTagData?.id) {
        requests.push(
          changePercentageAsync({
            namespaceId,
            tagId: disableTagData.id,
            percentage: 0,
          }),
          wipeTagAsync({
            namespaceId,
            tagId: disableTagData.id,
          })
        );
      }

      await Promise.all(requests);

      if (disableTagData?.id) {
        getTagQuery.invalidate({ namespaceId, tagId: disableTagData.id });
        getTagStatsQuery.invalidate({ namespaceId, tagId: disableTagData.id });
        listEntityTagsQuery.invalidate({
          namespaceId,
          tagId: disableTagData.id,
        });
      }

      getTagQuery.invalidate({ namespaceId, tagId });
      getTagStatsQuery.invalidate({ namespaceId, tagId });
    } catch (error) {
      alerts.error('Failed to allow all entities. Please try again.');
    }
  }, [
    alerts,
    changePercentageAsync,
    disableTagData?.id,
    namespaceId,
    tagId,
    wipeTagAsync,
  ]);

  const openPercentageModal = useCallback(
    (target: { tagId: string; currentPercentage?: number }) => {
      scrollPositionRef.current = window.scrollY;
      setPercentageModalTarget(target);
    },
    []
  );

  const closePercentageModal = useCallback(() => {
    setPercentageModalTarget(null);

    //Modal close restores focus to the initial scroll position (the scroll position before the modal was opened)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPositionRef.current });
      });
    });
  }, []);

  return (
    <Page
      title={tagId}
      breadcrumbs={getBreadcrumbs(location.pathname)}
      headerActions={
        <WetagsHeader>
          <PrimaryButton
            {...triggerProps}
            disabled={isDeleting}
            style={{
              width: 'auto',
              maxWidth: 'max-content',
            }}
            destructive
          >
            Delete Tag
          </PrimaryButton>
        </WetagsHeader>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
            padding: theme.spacing(1),
            borderRadius: theme.borderRadius.small,
            background: theme.colors.neutral5,
            height: 60,
          }}
        >
          <Heading level={3} css={{ margin: 0 }}>
            Details
          </Heading>
        </div>
        {!isTagLoading && tagData && <TagDataCard tagData={tagData} />}
        {tagData && (
          <AllowDenyTagManager
            namespaceId={namespaceId}
            allowTagId={tagId}
            denyTagId={disableTagData?.id}
            canCreateDenyList={!isDisableTag}
            allowPercentage={tagData?.percentage}
            allowExplicitlyTaggedCount={allowStatsData?.explicitlyTaggedEntitiesCount}
            denyPercentage={disableTagData?.percentage}
            denyExplicitlyTaggedCount={disableStatsData?.explicitlyTaggedEntitiesCount}
            isEnabled={disableTagData?.percentage !== 100}
            onEnable={enableTag}
            onDisable={onClickDisableTag}
            onAllowAll={onAllowAll}
            onCreateDenyList={() => createDisableTag(0)}
            onClickSetAllowPercentage={() => {
              openPercentageModal({
                tagId,
                currentPercentage: tagData?.percentage,
              });
            }}
            onClickSetDenyPercentage={() => {
              if (!disableTagData?.id) {
                return;
              }
              openPercentageModal({
                tagId: disableTagData.id,
                currentPercentage: disableTagData.percentage,
              });
            }}
            onClickAddAllow={() => setCreateModalTagId(tagId)}
            onClickAddDeny={() => {
              if (!disableTagData?.id) {
                return;
              }
              setCreateModalTagId(disableTagData.id);
            }}
          />
        )}
      </div>

      <ConfirmationModal
        {...modalProps}
        title="Confirm Delete"
        message={`Are you sure you want to delete the tag "${tagId}"? This action cannot be undone.`}
        onConfirm={onDelete}
      />

      <CreateEntityTagModal
        isOpen={!!createModalTagId}
        onClose={() => setCreateModalTagId(null)}
        namespaceId={namespaceId}
        tagId={createModalTagId ?? tagId}
      />

      <ChangeTagPercentageModal
        isOpen={!!percentageModalTarget}
        onClose={closePercentageModal}
        namespaceId={namespaceId}
        tagId={percentageModalTarget?.tagId ?? tagId}
        currentPercentage={percentageModalTarget?.currentPercentage}
      />
    </Page>
  );
}

const TagDataCard = ({ tagData }: { tagData: Tag }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        width: 500,
      }}
    >
      <DetailRow label="Namespace" value={tagData.namespaceId} />
      <DetailRow label="Description" value={tagData?.description} />
      {tagData.createTime && (
        <DetailRow
          label="Create time"
          value={<FormattedDate value={tagData.createTime} />}
        />
      )}
    </div>
  );
};
